/** 스캔 결과의 URL 조건과 원본 주소에서 계산한 선택지. 같은 축은 OR, 축 사이는 AND다. */
import type { Candidate } from "../domain/candidate";
import { DATASET_FORMATS, type DatasetFormat } from "../domain/dataset";

export type CandidateFilters = {
  search: string;
  formats: readonly DatasetFormat[];
  rootCodes: readonly string[];
  parentFolders: readonly string[];
};

export const EMPTY_CANDIDATE_FILTERS: CandidateFilters = {
  search: "",
  formats: [],
  rootCodes: [],
  parentFolders: [],
};

const AXES = ["formats", "rootCodes", "parentFolders"] as const;
export type CandidateFacetAxis = (typeof AXES)[number];
const PARAMS = { formats: "format", rootCodes: "root", parentFolders: "parent" } as const;

export function readCandidateFilters(params: URLSearchParams): CandidateFilters {
  return {
    search: (params.get("q") ?? "").trim(),
    formats: params
      .getAll("format")
      .filter((value): value is DatasetFormat => DATASET_FORMATS.includes(value as DatasetFormat)),
    rootCodes: params.getAll("root").filter(Boolean),
    parentFolders: params.getAll("parent").filter(Boolean),
  };
}

export function writeCandidateFilters(
  params: URLSearchParams,
  filters: CandidateFilters,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (filters.search.trim()) next.set("q", filters.search.trim());
  else next.delete("q");
  for (const axis of AXES) {
    next.delete(PARAMS[axis]);
    for (const value of filters[axis]) next.append(PARAMS[axis], value);
  }
  return next;
}

export function candidateParentFolder(sourceRef: string): string {
  try {
    const segments = new URL(sourceRef).pathname.split("/").filter(Boolean);
    return segments.length >= 2 ? segments[segments.length - 2] : "";
  } catch {
    return "";
  }
}

export function candidateFilterOptions(candidates: readonly Candidate[]) {
  const sorted = (values: readonly string[]) =>
    [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return {
    formats: DATASET_FORMATS.filter((format) =>
      candidates.some((candidate) => candidate.format === format),
    ),
    rootCodes: sorted(candidates.map((candidate) => candidate.rootCode)),
    parentFolders: sorted(
      candidates.map((candidate) => candidateParentFolder(candidate.sourceRef)),
    ),
  };
}

export function matchesCandidateFilters(candidate: Candidate, filters: CandidateFilters): boolean {
  const search = filters.search.trim().toLowerCase();
  return (
    (search === "" || candidate.sourceRef.toLowerCase().includes(search)) &&
    (filters.formats.length === 0 || filters.formats.includes(candidate.format)) &&
    (filters.rootCodes.length === 0 || filters.rootCodes.includes(candidate.rootCode)) &&
    (filters.parentFolders.length === 0 ||
      filters.parentFolders.includes(candidateParentFolder(candidate.sourceRef)))
  );
}
